import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  PlayCircle,
  ClipboardCheck,
  History,
  ChevronDown,
  ChevronRight,
  Clock,
} from 'lucide-react';

import { useI18n } from '@/i18n/LanguageContext';
import { CHANGE_AREAS, AreaId, ReleaseCheck, areaLabel, checkById } from '@/data/releaseChecks';
import {
  CheckResult,
  ReleaseRun,
  ResultStatus,
  loadRuns,
  orderedChecksForAreas,
  runCheck,
  saveRun,
  verdictFor,
} from '@/lib/releaseChecks';

/**
 * ADMIN → RELEASE CHECKS
 * ======================
 * The pre-deploy gate. Pick the areas a change touched; the panel derives the
 * suites that must be green (from src/data/releaseChecks.ts — the same registry
 * the runner uses), runs them, and records the verdict to mkt_release_runs.
 *
 * It does not replace the detailed panels — each result names the tab where the
 * full evidence lives. Its job is to make "was this validated?" a question with
 * a recorded answer instead of a memory.
 */

const Icon: React.FC<{ status: ResultStatus | 'running' | 'pending' }> = ({ status }) => {
  if (status === 'running') return <Loader2 className="h-5 w-5 shrink-0 animate-spin text-slate-400" aria-hidden />;
  if (status === 'pending') return <Clock className="h-5 w-5 shrink-0 text-slate-300" aria-hidden />;
  if (status === 'pass') return <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" aria-hidden />;
  if (status === 'attention') return <AlertTriangle className="h-5 w-5 shrink-0 text-amber-500" aria-hidden />;
  return <XCircle className="h-5 w-5 shrink-0 text-red-600" aria-hidden />;
};

const rowTone: Record<string, string> = {
  pass: 'border-emerald-200 bg-emerald-50/50',
  attention: 'border-amber-200 bg-amber-50/50',
  fail: 'border-red-200 bg-red-50/50',
  error: 'border-red-200 bg-red-50/50',
  running: 'border-slate-300 bg-white',
  pending: 'border-slate-200 bg-white',
};

const AdminReleaseChecks: React.FC = () => {
  const { lang } = useI18n();
  const es = lang === 'es';

  const [areas, setAreas] = useState<AreaId[]>([]);
  const [note, setNote] = useState('');
  const [results, setResults] = useState<Record<string, CheckResult>>({});
  const [running, setRunning] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [history, setHistory] = useState<ReleaseRun[]>([]);
  // True only when the CURRENT set of results was written to mkt_release_runs.
  // Running suites one at a time does not create a release record, and the
  // banner must not claim it did.
  const [recorded, setRecorded] = useState(false);

  const required = useMemo(() => orderedChecksForAreas(areas), [areas]);

  const refreshHistory = useCallback(async () => {
    setHistory(await loadRuns(6));
  }, []);

  useEffect(() => {
    void refreshHistory();
  }, [refreshHistory]);

  const toggleArea = (id: AreaId) =>
    setAreas((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));

  const runAll = async () => {
    setBusy(true);
    setResults({});
    setRecorded(false);
    const collected: CheckResult[] = [];
    for (const check of required) {
      setRunning(check.id);
      const r = await runCheck(check);
      collected.push(r);
      setResults((prev) => ({ ...prev, [check.id]: r }));
      if (r.status !== 'pass') setOpen((prev) => ({ ...prev, [check.id]: true }));
    }
    setRunning(null);
    setBusy(false);
    if (collected.length) {
      const saved = await saveRun({ areas, changeNote: note, results: collected })
        .then(() => true)
        .catch(() => false);
      setRecorded(saved);
      void refreshHistory();
    }
  };

  const runOne = async (check: ReleaseCheck) => {
    setBusy(true);
    setRunning(check.id);
    // A single suite is a spot check, not a release record.
    setRecorded(false);
    const r = await runCheck(check);
    setResults((prev) => ({ ...prev, [check.id]: r }));
    if (r.status !== 'pass') setOpen((prev) => ({ ...prev, [check.id]: true }));
    setRunning(null);
    setBusy(false);
  };

  const collected = required.map((c) => results[c.id]).filter(Boolean) as CheckResult[];
  const complete = collected.length === required.length && required.length > 0;
  const verdict = complete ? verdictFor(collected) : 'unknown';

  const recordLine = recorded
    ? es
      ? ' La ejecución quedó registrada.'
      : ' The run has been recorded.'
    : es
      ? ' Aún NO se ha registrado: pulse “Ejecutar” para guardar la evidencia.'
      : ' This is NOT recorded yet — press Run to store the evidence.';

  const banner =
    verdict === 'clear'
      ? {
          cls: 'border-emerald-300 bg-emerald-50 text-emerald-900',
          title: es ? 'LISTO PARA DESPLEGAR' : 'CLEAR TO SHIP',
          body:
            (es
              ? 'Todas las pruebas requeridas para las áreas seleccionadas pasaron.'
              : 'Every suite required for the selected areas passed.') + recordLine,
        }
      : verdict === 'review'
        ? {
            cls: 'border-amber-300 bg-amber-50 text-amber-900',
            title: es ? 'REVISIÓN MANUAL' : 'NEEDS REVIEW',
            body:
              (es
                ? 'Nada bloqueante falló, pero al menos una prueba necesita revisión humana antes de considerar el cambio completo.'
                : 'Nothing blocking failed, but at least one suite needs a human decision before this change is complete.') +
              recordLine,
          }
        : {
            cls: 'border-red-300 bg-red-50 text-red-900',
            title: es ? 'NO DESPLEGAR' : 'DO NOT SHIP',
            body:
              (es
                ? 'Una prueba requerida falló o no respondió. Esa área no está validada.'
                : 'A required suite failed or could not be reached. That area is not validated.') + recordLine,
          };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5">
      <h2 className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-slate-900">
        <ClipboardCheck className="h-4 w-4 text-orange-500" />
        {es ? 'Comprobaciones previas al despliegue' : 'Pre-deploy release checks'}
      </h2>
      <p className="mt-1 max-w-3xl text-xs text-slate-500">
        {es
          ? 'Marque las áreas que toca el cambio. El panel deriva las pruebas obligatorias, las ejecuta y guarda el veredicto. Las pruebas no son nuevas: son las mismas de Créditos, Pagos, Webhook y Seguridad, reunidas aquí con la regla de qué hay que ejecutar y cuándo.'
          : 'Tick the areas the change touched. The panel derives the mandatory suites, runs them, and records the verdict. The suites are not new — they are the same ones under Credits, Payments, Webhook and Security, gathered here with the rule for what must run when.'}
      </p>

      {/* ---------------- area picker ---------------- */}
      <div className="mt-5 flex flex-wrap gap-2">
        {CHANGE_AREAS.map((a) => {
          const on = areas.includes(a.id);
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => toggleArea(a.id)}
              title={a.examplesEn}
              className={`rounded-full border px-3.5 py-2 text-xs font-bold transition-colors ${
                on
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-300 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {es ? a.es : a.en}
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-[11px] text-slate-400">
        {areas.length === 0
          ? es
            ? 'Sin selección se ejecuta la batería completa.'
            : 'With nothing selected, the full battery runs.'
          : `${es ? 'Áreas' : 'Areas'}: ${areas.map((a) => areaLabel(a, es)).join(' · ')}`}
      </p>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="min-w-[240px] flex-1">
          <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
            {es ? 'Qué cambió (queda en el registro)' : 'What changed (stored with the run)'}
          </span>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={es ? 'ej. nuevo precio del plan pro' : 'e.g. new pro plan price'}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
        </label>
        <button
          type="button"
          onClick={runAll}
          disabled={busy || required.length === 0}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-slate-800 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
          {busy
            ? es
              ? 'Ejecutando…'
              : 'Running…'
            : es
              ? `Ejecutar ${required.length} prueba(s)`
              : `Run ${required.length} required suite(s)`}
        </button>
      </div>

      {complete && (
        <div className={`mt-5 rounded-xl border px-4 py-3 ${banner.cls}`}>
          <p className="text-sm font-extrabold">{banner.title}</p>
          <p className="mt-0.5 text-xs">{banner.body}</p>
        </div>
      )}

      {/* ---------------- required suites ---------------- */}
      <div className="mt-5 space-y-2">
        {required.map((c) => {
          const r = results[c.id];
          const status: ResultStatus | 'running' | 'pending' = running === c.id ? 'running' : r ? r.status : 'pending';
          const isOpen = !!open[c.id];
          return (
            <div key={c.id} className={`rounded-xl border px-4 py-3 ${rowTone[status] || rowTone.pending}`}>
              <div className="flex items-start gap-2">
                <Icon status={status} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-bold text-slate-900">{es ? c.es : c.en}</p>
                    {c.duration === 'slow' && (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">
                        {es ? 'lenta' : 'slow'}
                      </span>
                    )}
                    {c.writesFixtures && (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">
                        {es ? 'datos sintéticos' : 'synthetic fixtures'}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">{c.provesEn}</p>
                  {r && (
                    <p
                      className={`mt-1.5 text-xs font-bold ${
                        r.status === 'pass'
                          ? 'text-emerald-700'
                          : r.status === 'attention'
                            ? 'text-amber-700'
                            : 'text-red-700'
                      }`}
                    >
                      {r.summary} · {r.ms} ms
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => runOne(c)}
                      disabled={busy}
                      className="text-xs font-bold text-slate-700 underline hover:text-slate-900 disabled:opacity-50"
                    >
                      {es ? 'Ejecutar sólo esta' : 'Run this one'}
                    </button>
                    {r && r.detail.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setOpen((p) => ({ ...p, [c.id]: !isOpen }))}
                        className="inline-flex items-center gap-1 text-xs font-bold text-slate-700 hover:text-slate-900"
                      >
                        {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        {es ? 'Evidencia' : 'Evidence'} ({r.detail.length})
                      </button>
                    )}
                    <span className="text-[11px] text-slate-400">
                      {es ? 'Panel completo:' : 'Full panel:'} {c.fullPanelEn}
                    </span>
                  </div>
                  {isOpen && r && (
                    <ul className="mt-2 space-y-1 rounded-lg bg-white/70 p-3 font-mono text-[11px] text-slate-600">
                      {r.detail.map((d, i) => (
                        <li key={i} className="break-words">
                          {d}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ---------------- recorded history ---------------- */}
      <h3 className="mt-8 flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wide text-slate-500">
        <History className="h-3.5 w-3.5" />
        {es ? 'Ejecuciones registradas' : 'Recorded runs'}
      </h3>
      {history.length === 0 ? (
        <p className="mt-2 text-xs text-slate-400">
          {es ? 'Todavía no hay ejecuciones guardadas.' : 'No runs recorded yet.'}
        </p>
      ) : (
        <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5">{es ? 'Cuándo' : 'When'}</th>
                <th className="px-4 py-2.5">{es ? 'Áreas' : 'Areas'}</th>
                <th className="px-4 py-2.5">{es ? 'Resultado' : 'Result'}</th>
                <th className="px-4 py-2.5">{es ? 'Cambio' : 'Change'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {history.map((h, i) => (
                <tr key={h.id || i}>
                  <td className="px-4 py-2.5 text-xs text-slate-500">
                    {h.created_at ? new Date(h.created_at).toLocaleString() : '—'}
                    <span className="block text-slate-400">{h.run_by_email || ''}</span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">
                    {(h.areas || []).map((a) => areaLabel(a, es)).join(' · ') || (es ? 'todas' : 'all')}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                        h.verdict === 'clear'
                          ? 'bg-emerald-50 text-emerald-700'
                          : h.verdict === 'review'
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-red-50 text-red-700'
                      }`}
                    >
                      {h.passed_checks}/{h.total_checks} {es ? 'pasaron' : 'passed'}
                    </span>
                    {(h.results || [])
                      .filter((r) => r.status === 'fail' || r.status === 'error')
                      .map((r) => (
                        <span key={r.id} className="mt-1 block text-[11px] text-red-700">
                          {checkById(r.id)?.en || r.id}: {r.summary}
                        </span>
                      ))}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">{h.change_note || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default AdminReleaseChecks;
